import { Receipt, SpinnerGap, X } from '@phosphor-icons/react';
import { Input } from 'antd';
import { ButtonCommon, LoadingFallback, StickyToolbarWrapper, ToolbarForLayoutTwoCol } from 'components/common';
import { InfoField, InfoGrid, ListProduct, ProductDetailForm } from 'components/PurchaseOrder';
import { useCreateCheckOrder } from 'hooks/useCreateCheckOrderOperations';
import { useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';

function CreateCheckOrderPage() {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery({ minWidth: 1280 });
  const [isDetailPanelVisible, setIsDetailPanelVisible] = useState(false);

  const {
    checkOrderData,
    form,
    selectedProducts,
    selectedProduct,
    description,
    setDescription,
    productVersionsData,
    isLoadingProducts,
    handleSelectProductToList,
    handleRemoveProduct,
    handleProductSearch,
    handleSaveActualQuantity,
    handleSelectProduct,
    handleSubmit,
    profile,
    loading,
    isLoading,
  } = useCreateCheckOrder();

  if (loading || !profile) {
    return <LoadingFallback />;
  }

  const toggleDetailPanel = () => {
    setIsDetailPanelVisible((prev) => !prev);
  };

  return (
    <div className='flex flex-col bg-[var(--textBlack2)] h-[100vh]'>
      {isLoading && (
        <div className='fixed bg-black/20 flex items-center justify-center z-50 rounded-lg top-0 left-0 w-full h-full'>
          <div className='bg-white rounded-lg p-6 shadow-lg flex flex-col items-center'>
            <SpinnerGap size={32} className='text-[var(--primaryColor500)] animate-spin mb-3' />
            <p className='text-gray-700 font-medium'>{`Đang cập nhật trạng thái`}</p>
          </div>
        </div>
      )}

      <StickyToolbarWrapper isHandleScroll={false} stickyTop={56}>
        <ToolbarForLayoutTwoCol
          form={form}
          isLoading={loading}
          currentStep={0}
          listVersion={[]}
          setIsActive={() => {}}
          statusLabel='Đang kiểm kho'
          createOrderSupplier={
            checkOrderData?.data?.createdAt ? (checkOrderData?.data?.createdAt as unknown as string) : undefined
          }
          customButton={
            <>
              <ButtonCommon
                color='none'
                size='sm'
                radius='rounded'
                onClick={() => {
                  navigate('/management/inventory/check-orders/');
                }}
              >
                Hủy
              </ButtonCommon>
              <ButtonCommon
                loading={isLoading}
                color='soft'
                radius='rounded'
                size='sm'
                onClick={() => handleSubmit(false)}
              >
                Lưu đợi kiểm kho sau
              </ButtonCommon>
              <ButtonCommon
                loading={isLoading}
                color='primary'
                radius='rounded'
                size='sm'
                onClick={() => handleSubmit(true)}
              >
                Hoàn thành kiểm kho
              </ButtonCommon>
            </>
          }
        />
      </StickyToolbarWrapper>

      {/* Main Content */}
      <div className='flex flex-1 overflow-hidden'>
        {/* Left Panel - Product List */}
        <div className='flex-1 overflow-auto p-4'>
          <div className='bg-white max-h-[calc(100vh-150px)] min-h-[calc(100vh-150px)] rounded-lg shadow-sm h-full'>
            <ListProduct
              products={[]}
              title={'DANH SÁCH SẢN PHẨM ĐÃ CHỌN'}
              onSelectProduct={handleSelectProduct}
              availableProductVersions={productVersionsData?.data?.rows || []}
              onSelectProductToList={handleSelectProductToList}
              selectedProducts={selectedProducts}
              onRemoveProduct={handleRemoveProduct}
              isLoadingProducts={isLoadingProducts}
              onProductSearch={handleProductSearch}
            />
          </div>
        </div>

        {/* Right Panel - Product Detail (Desktop) */}
        {isDesktop && (
          <div className='w-[444px] p-4 overflow-auto'>
            {selectedProduct ? (
              <ProductDetailForm
                key={selectedProduct?.id}
                product={selectedProduct}
                onSave={handleSaveActualQuantity}
                className='w-full h-full 2xl:!w-full xl:!w-full lg:!w-full max-h-[calc(100vh-150px)]'
                hasDescription={true}
              />
            ) : (
              <div className='max-h-[calc(100vh-150px)] h-full w-full bg-white rounded-lg shadow-sm px-6 py-3'>
                <h2 className='text-[var(--primaryColor)] text-xl font-bold'>THÔNG TIN PHIẾU KIỂM</h2>
                <InfoGrid columns={2} className='!p-0 my-6'>
                  <InfoField label='Mã phiếu:' value={form.getFieldValue('code')} isHighlight={true} />
                  <InfoField label='Người tạo:' value={profile?.data.baseUser?.name || ''} isHighlight={true} />
                </InfoGrid>
                <div className='mt-2'>
                  <span>Mô tả</span>
                  <Input.TextArea
                    value={description}
                    className='mt-1 rounded-md p-2'
                    rows={5}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder='Nhập mô tả cho phiếu...'
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Mobile Detail Panel Button */}
      {!isDesktop && (
        <div className='fixed bottom-6 right-6 z-40 cursor-pointer' onClick={toggleDetailPanel}>
          <div className='bg-[var(--primaryColor500)] text-white rounded-full w-16 h-16 flex items-center justify-center shadow-lg'>
            <Receipt size={28} weight='fill' />
          </div>
        </div>
      )}

      {/* Mobile Detail Panel Overlay */}
      {!isDesktop && (
        <div className={`fixed inset-0 z-50 flex justify-end ${isDetailPanelVisible ? 'visible' : 'invisible'}`}>
          <div
            className={`absolute inset-0 bg-black transition-opacity duration-300 ${
              isDetailPanelVisible ? 'opacity-50' : 'opacity-0'
            }`}
            onClick={() => setIsDetailPanelVisible(false)}
          ></div>

          <div
            className={`relative bg-white min-w-[500px] flex flex-col max-w-[550px] h-full transform transition-transform duration-300 ease-out shadow-xl overflow-auto ${
              isDetailPanelVisible ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            <div className='sticky top-0 bg-white p-4 border-b border-gray-200 flex items-center justify-between z-10'>
              <div className='text-lg font-bold'>Chi tiết sản phẩm</div>
              <button
                className='text-gray-500 hover:text-gray-700 focus:outline-none'
                onClick={() => setIsDetailPanelVisible(false)}
              >
                <X size={24} />
              </button>
            </div>
            <div className='p-4 flex-1'>
              {selectedProduct ? (
                <ProductDetailForm
                  key={selectedProduct?.id}
                  product={selectedProduct}
                  onSave={handleSaveActualQuantity}
                  className='w-full h-full'
                  hasDescription={true}
                />
              ) : (
                <div className='h-full w-full bg-white rounded-lg shadow-sm px-6 py-3'>
                  <h2 className='text-[var(--primaryColor)] text-xl font-bold'>THÔNG TIN PHIẾU KIỂM</h2>
                  <InfoGrid columns={1} className='!p-0 my-6'>
                    <InfoField label='Mã phiếu:' value={form.getFieldValue('code')} isHighlight={true} />
                    <InfoField label='Người tạo:' value={profile?.data.baseUser?.name || ''} isHighlight={true} />
                  </InfoGrid>
                  <div className='mt-2'>
                    <span>Mô tả</span>
                    <Input.TextArea
                      value={description}
                      className='mt-1 rounded-md p-2'
                      rows={5}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder='Nhập mô tả cho phiếu...'
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CreateCheckOrderPage;
